import { ApiClientError, NetworkError } from '@recipes/api-client';
import type {
  CatalogSyncResponse,
  Ingredient,
  PriceObservation,
  Product,
  PurchaseHint,
  Store,
} from '@recipes/contracts';
import { normalizeIngredientName } from '@recipes/domain';
import { api } from '@/lib/api';
import { authStore } from '@/lib/auth/auth-store';
import {
  offlineDb,
  type CatalogHintRow,
  type CatalogPriceRow,
  type CatalogIngredientRow,
  type CatalogProductRow,
} from '@/lib/offline/db';
import { getPlatform } from '@/lib/platform';

/**
 * Device copy of the household's catalog (docs/finished/ingredient-catalog-spec.md 6.9). Whatever is
 * needed at the shelf is here before the user gets there: products with barcodes, hints,
 * stores, ingredients and the dietary settings. Filled by a delta sync that runs at sign-in,
 * on app start, when connectivity returns and on resume – never only when a list is opened.
 */

export interface CatalogSyncState {
  cursor: string | null;
  syncedAt: string | null;
  userId: string | null;
}

export interface DietarySettings {
  dietaryPreferences: string[];
  avoidedIngredients: string[];
}

const META_STATE = 'sync-state';
const META_DIETARY = 'dietary';
const EMPTY_STATE: CatalogSyncState = { cursor: null, syncedAt: null, userId: null };
const MAX_PAGES = 40;
const THUMB_LIMIT = 300;

const listeners = new Set<() => void>();
export function subscribeCatalog(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}
export const notifyCatalog = () => listeners.forEach((l) => l());

export async function catalogSyncState(): Promise<CatalogSyncState> {
  const row = await offlineDb.catalogMeta.get(META_STATE);
  return (row?.value as CatalogSyncState | undefined) ?? EMPTY_STATE;
}

export async function dietarySettings(): Promise<DietarySettings> {
  const row = await offlineDb.catalogMeta.get(META_DIETARY);
  return (
    (row?.value as DietarySettings | undefined) ?? {
      dietaryPreferences: [],
      avoidedIngredients: [],
    }
  );
}

export const toIngredientRow = (ingredient: Ingredient): CatalogIngredientRow => ({
  id: ingredient.id,
  normalizedName: ingredient.normalizedName,
  aliasKeys: ingredient.aliases.map((a) => normalizeIngredientName(a.name)).filter(Boolean),
  ingredient,
});

export const toProductRow = (
  product: Product,
  origin: CatalogProductRow['origin'],
): CatalogProductRow => ({
  id: product.id,
  barcode: product.barcode,
  ingredientId: product.ingredient?.id ?? null,
  origin,
  product,
  savedAt: Date.now(),
});

export const toPriceRow = (price: PriceObservation, pending = false): CatalogPriceRow => ({
  id: price.id,
  productId: price.productId,
  ingredientId: price.ingredientId,
  storeId: price.storeId,
  chainId: price.chainId,
  price,
  ...(pending ? { pending } : {}),
});

export const toHintRow = (hint: PurchaseHint, pending = false): CatalogHintRow => ({
  id: hint.id,
  ingredientId: hint.ingredientId,
  productId: hint.productId,
  storeId: hint.storeId,
  hint,
  pending,
});

/** Removes everything, e.g. when another account signs in on this device. */
export async function wipeCatalogCopy(): Promise<void> {
  await Promise.all([
    offlineDb.catalogIngredients.clear(),
    offlineDb.catalogProducts.clear(),
    offlineDb.catalogStores.clear(),
    offlineDb.catalogHints.clear(),
    offlineDb.catalogThumbs.clear(),
    offlineDb.catalogMeta.clear(),
    offlineDb.catalogPrices.clear(),
    offlineDb.pendingTripPhotos.clear(),
    offlineDb.catalogLoyaltyCards.clear(),
    offlineDb.catalogCardImages.clear(),
  ]);
}

/** Rows this young survive a restart of the copy: the page may predate them. */
const RESTART_GRACE_MS = 60_000;

async function applyPage(
  page: CatalogSyncResponse,
  userId: string,
  requestedAt: number,
): Promise<void> {
  const pendingIds = new Set(
    (await offlineDb.pendingCatalog.toArray()).map((action) => action.entityId),
  );
  await offlineDb.transaction(
    'rw',
    [
      offlineDb.catalogIngredients,
      offlineDb.catalogProducts,
      offlineDb.catalogStores,
      offlineDb.catalogHints,
      offlineDb.catalogMeta,
      offlineDb.catalogPrices,
      offlineDb.catalogLoyaltyCards,
    ],
    async () => {
      if (page.full) {
        // Keep what this device looked up or captured itself; the household copy restarts.
        await offlineDb.catalogIngredients.clear();
        // …except rows written while the request was under way (a product captured or a hint
        // saved a moment ago is not in this page yet); the next delta sync reconciles them.
        const keepSince = new Date(requestedAt - RESTART_GRACE_MS).toISOString();
        await offlineDb.catalogProducts
          .where('origin')
          .equals('sync')
          .filter((row) => row.product.updatedAt < keepSince)
          .delete();
        await offlineDb.catalogStores
          .filter((row) => !row.pending && row.store.updatedAt < keepSince)
          .delete();
        await offlineDb.catalogHints
          .filter((row) => !row.pending && row.hint.updatedAt < keepSince)
          .delete();
        await offlineDb.catalogPrices
          .filter((row) => !row.pending && row.price.updatedAt < keepSince)
          .delete();
        await offlineDb.catalogLoyaltyCards
          .filter((row) => !row.pending && row.card.updatedAt < keepSince)
          .delete();
      }
      // Rows with a queued local change are not overwritten – the queue has the newer truth.
      const fresh = <T extends { id: string }>(rows: T[]) =>
        rows.filter((r) => !pendingIds.has(r.id));
      await offlineDb.catalogIngredients.bulkPut(page.ingredients.map(toIngredientRow));
      await offlineDb.catalogProducts.bulkPut(
        fresh(page.products).map((p: Product) => toProductRow(p, 'sync')),
      );
      await offlineDb.catalogStores.bulkPut(
        fresh(page.stores).map((store: Store) => ({ id: store.id, store })),
      );
      await offlineDb.catalogHints.bulkPut(fresh(page.hints).map((h) => toHintRow(h)));
      await offlineDb.catalogPrices.bulkPut(
        fresh(page.priceObservations ?? []).map((price) => toPriceRow(price)),
      );
      await offlineDb.catalogLoyaltyCards.bulkPut(
        fresh(page.loyaltyCards ?? []).map((card) => ({ id: card.id, card })),
      );
      for (const tombstone of page.tombstones) {
        if (tombstone.entity === 'ingredient')
          await offlineDb.catalogIngredients.delete(tombstone.id);
        if (tombstone.entity === 'product') await offlineDb.catalogProducts.delete(tombstone.id);
        if (tombstone.entity === 'store') await offlineDb.catalogStores.delete(tombstone.id);
        if (tombstone.entity === 'hint') await offlineDb.catalogHints.delete(tombstone.id);
        if (tombstone.entity === 'price') await offlineDb.catalogPrices.delete(tombstone.id);
        if (tombstone.entity === 'loyaltyCard')
          await offlineDb.catalogLoyaltyCards.delete(tombstone.id);
      }
      await offlineDb.catalogMeta.put({ key: META_DIETARY, value: page.dietary });
      await offlineDb.catalogMeta.put({
        key: META_STATE,
        value: { cursor: page.cursor, syncedAt: page.syncedAt, userId } satisfies CatalogSyncState,
      });
    },
  );
}

/** Small thumbnails as blobs, so product cards have a picture without a connection. */
async function cacheThumbnails(): Promise<void> {
  const have = new Set((await offlineDb.catalogThumbs.toCollection().primaryKeys()) as string[]);
  const rows = await offlineDb.catalogProducts.toArray();
  let budget = THUMB_LIMIT - have.size;
  for (const row of rows) {
    if (budget <= 0) break;
    const url = row.product.photos[0]?.thumbnailUrl;
    if (!url || have.has(row.id)) continue;
    try {
      const response = await fetch(url);
      if (!response.ok) continue;
      const blob = await response.blob();
      // Thumbnails are a few kilobytes; anything large is not worth the space.
      if (blob.size > 60_000) continue;
      await offlineDb.catalogThumbs.put({ productId: row.id, blob, savedAt: Date.now() });
      budget--;
    } catch {
      return; // offline or blocked: try again with the next sync
    }
  }
}

/** A card picture above this is not a photo of a card; it stays on the server. */
const CARD_IMAGE_LIMIT_BYTES = 3_000_000;

/**
 * The pictures of the loyalty cards as blobs: the scanner at the checkout must read them
 * without a connection. Fetched once per asset; a picture replaced on another device is
 * fetched again, the picture of a deleted card goes.
 */
async function cacheCardImages(): Promise<void> {
  const rows = await offlineDb.catalogLoyaltyCards.toArray();
  const wanted = new Set(rows.map((row) => row.id));
  const stale = (await offlineDb.catalogCardImages.toArray()).filter(
    (image) => !wanted.has(image.cardId),
  );
  if (stale.length) await offlineDb.catalogCardImages.bulkDelete(stale.map((s) => s.cardId));
  for (const row of rows) {
    const image = row.card.image;
    if (!image?.url || (image.status !== 'READY' && image.status !== 'UPLOADED')) continue;
    const have = await offlineDb.catalogCardImages.get(row.id);
    // The picture taken on this device waits for its upload: keep it until the asset is known.
    if (have && (have.assetId === image.assetId || have.assetId === null)) continue;
    try {
      const response = await fetch(image.url);
      if (!response.ok) continue;
      const blob = await response.blob();
      if (blob.size > CARD_IMAGE_LIMIT_BYTES) continue;
      await offlineDb.catalogCardImages.put({
        cardId: row.id,
        assetId: image.assetId,
        blob,
        savedAt: Date.now(),
      });
    } catch {
      return; // offline or blocked: try again with the next sync
    }
  }
}

export interface CatalogSyncResult {
  ok: boolean;
  offline: boolean;
  pages: number;
}

let running: Promise<CatalogSyncResult> | null = null;

/** Pulls the changes since the last sync. Safe to call often; concurrent calls share one run. */
export function syncCatalog(): Promise<CatalogSyncResult> {
  if (running) return running;
  running = (async (): Promise<CatalogSyncResult> => {
    const userId = authStore.getState().user?.id ?? null;
    if (!userId) return { ok: false, offline: false, pages: 0 };
    let state = await catalogSyncState();
    if (state.userId && state.userId !== userId) {
      await wipeCatalogCopy();
      state = EMPTY_STATE;
    }
    let pages = 0;
    try {
      let cursor = state.cursor ?? undefined;
      for (;;) {
        const requestedAt = Date.now();
        const page = await api.catalog.sync(cursor);
        await applyPage(page, userId, requestedAt);
        pages++;
        cursor = page.cursor;
        if (!page.hasMore || pages >= MAX_PAGES) break;
      }
      void cacheThumbnails();
      void cacheCardImages().then(notifyCatalog);
      // Storage a browser may evict under pressure is not good enough for the supermarket.
      void navigator.storage?.persist?.().catch(() => undefined);
      notifyCatalog();
      return { ok: true, offline: false, pages };
    } catch (error) {
      const offline =
        error instanceof NetworkError || (error instanceof ApiClientError && error.status === 401);
      if (pages > 0) notifyCatalog();
      return { ok: false, offline, pages };
    }
  })().finally(() => {
    running = null;
  });
  return running;
}

let started = false;

/** While the app stays open, the copy is refreshed and the queue retried this often. */
export const CATALOG_REFRESH_MS = 60_000;

/**
 * Idempotent: sync now, when connectivity returns, when the app comes back – and every minute
 * while it is open. The minute is for the phone lying open on the counter while a card or a
 * store is added on the desktop, and for a change captured offline that must leave the
 * device once it is back online without a reload (user, 2026-09-25). A delta sync that finds
 * nothing costs one small request.
 */
export function startCatalogSync(onOnline?: () => void): void {
  if (started || typeof window === 'undefined') return;
  started = true;
  void getPlatform().then((platform) => {
    const run = () => {
      if (!platform.isOnline()) return;
      onOnline?.();
      void syncCatalog();
    };
    platform.onConnectivityChange((online) => {
      if (online) run();
    });
    platform.onResume(run);
    window.addEventListener('focus', run);
    window.setInterval(() => {
      if (document.visibilityState === 'visible') run();
    }, CATALOG_REFRESH_MS);
    run();
  });
}

/** Storage used by this origin, for the settings page. */
export async function catalogStorageEstimate(): Promise<{
  usageBytes: number | null;
  quotaBytes: number | null;
  persisted: boolean | null;
  products: number;
  hints: number;
  stores: number;
}> {
  const estimate = await navigator.storage?.estimate?.().catch(() => undefined);
  const persisted = (await navigator.storage?.persisted?.().catch(() => undefined)) ?? null;
  const [products, hints, stores] = await Promise.all([
    offlineDb.catalogProducts.count(),
    offlineDb.catalogHints.count(),
    offlineDb.catalogStores.count(),
  ]);
  return {
    usageBytes: estimate?.usage ?? null,
    quotaBytes: estimate?.quota ?? null,
    persisted,
    products,
    hints,
    stores,
  };
}
